"use client";

import { useEffect, useState } from "react";

import FlickeringGrid from "@/components/magicui/flickeringGrid";
import { Icons } from "@/components/shared/icons";
import { Button, buttonVariants } from "@/components/ui/button";
import { pricingData } from "@/config/subscriptions";
import { Link } from "@/lib/i18n";
import { getUserSubscriptionPlan } from "@/lib/subscription";
import { cn } from "@/lib/utils";
import * as m from "@/paraglide/messages";
import { useAuth } from "@/services/context/auth/AuthContext";
import { UserSubscriptionPlan } from "@/types";
import { SubscriptionPlan } from "@/types/index";

import { BillingFormButton } from "./billing-form-button";

export function PricingCardsAlt() {
  const { user } = useAuth();
  const [subscriptionPlan, setSubscriptionPlan] =
    useState<UserSubscriptionPlan>();

  useEffect(() => {
    const controller = new AbortController();
    if (user?.uid)
      getUserSubscriptionPlan({ controller }).then((res) => {
        setSubscriptionPlan(res);
      });
    return () => {
      controller.abort();
    };
  }, [user?.uid]);

  const PricingCard = ({ offer }: { offer: SubscriptionPlan }) => {
    if (offer.title === m.starter_label()) {
      return (
        <div className="text-left dark:bg-background relative flex flex-col overflow-hidden rounded-xl border shadow-sm order-last md:order-first">
          <div className="min-h-[150px] items-start space-y-6  p-6">
            <div className="text-left">
              <h3
                className="text-sm flex  font-bold uppercase tracking-wider"
                key={offer.title}
              >
                {offer.title}
              </h3>
              <p className="text-xs text-muted-foreground">
                {m.plane_tasty_okapi_promise()}
              </p>
            </div>
            <div className="space-y-1">
              <div className="flex flex-row">
                <div className="flex items-end">
                  <div className="flex text-left text-3xl font-semibold leading-6">
                    {offer.prices.yearly > 0
                      ? `€${offer.prices.yearly}`
                      : m.free_forever()}
                  </div>
                  {offer.prices.yearly > 0 && (
                    <div className="-mb-1 ml-2 text-left text-sm font-medium text-muted-foreground">
                      <div>/{m.yearly()}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {
              // loadingSub ? (
              //   <Skeleton className="w-40 h-[40px] bg-muted-foreground opacity-20" />
              // ) :
              user?.email && subscriptionPlan ? (
                offer.title === "Starter" ? (
                  <Link
                    href="/dashboard"
                    className={cn(
                      buttonVariants({
                        variant: "default",
                        size: "sm",
                      }),
                      "",
                    )}
                  >
                    {m.my_portfolio()}
                  </Link>
                ) : (
                  <BillingFormButton
                    offer={offer}
                    year={true}
                    subscriptionPlan={subscriptionPlan}
                  />
                )
              ) : (
                <Link href="/signup" className="block">
                  <Button variant="default" size="sm" className="block">
                    {m.get_started()}
                  </Button>
                </Link>
              )
            }
          </div>

          <div className="min-h-[150px] items-start space-y-4  p-6">
            <h4 className="font-bold">{m.discover()}</h4>
            <ul className="space-y-2 text-left text-sm font-medium leading-normal">
              <li className="flex items-start gap-x-3">
                <Icons.check className="size-5 shrink-0 text-purple-500" />
                <p>{m.caring_white_shell_pray()}</p>
              </li>
              <li className="flex items-start gap-x-3">
                <Icons.check className="size-5 shrink-0 text-purple-500" />
                <p>{m.crazy_whole_mouse_edit()}</p>
              </li>
              <li className="flex items-start gap-x-3">
                <Icons.check className="size-5 shrink-0 text-purple-500" />
                <p>{m.safe_major_owl_inspire()}</p>
              </li>
              <li className="flex items-start text-muted-foreground">
                <Icons.close className="mr-3 size-5 shrink-0" />
                <p>{m.mushy_patchy_clownfish_fulfill()}</p>
              </li>
            </ul>
          </div>
        </div>
      );
    } else if (offer.title === m.premium_label()) {
      return (
        <div className="shadow-zinc-900 dark:bg-white bg-black relative flex flex-col md:flex-row flex-wrap overflow-hidden rounded-xl border-zinc-900 shadow-sm md:col-span-2">
          <FlickeringGrid
            className={cn(
              "[mask-image:radial-gradient(circle_at_top_left,white,transparent)] z-0 absolute inset-0 size-full",
            )}
            squareSize={2}
            gridGap={8}
            color="#6B7280"
            maxOpacity={0.3}
            flickerChance={0.8}
          />
          <div className="text-left flex flex-col gap-2  md:w-1/2 min-h-[150px] z-10 items-start p-6">
            <div className="text-left">
              <h3
                className="text-sm flex text-muted font-bold uppercase tracking-wider"
                key={offer.title}
              >
                {offer.title}
              </h3>
              <p className="text-xs text-muted-foreground">
                {m.glad_fluffy_cowfish_bend()}
              </p>
            </div>
            <div className="flex flex-col gap-2 mt-2">
              <div className="flex flex-row">
                <div className="flex items-end">
                  <div className="flex text-left text-3xl font-semibold leading-6 text-muted">
                    €{offer.prices.yearly}
                  </div>
                  <div className="-mb-1 ml-2 text-left text-sm font-medium text-muted-foreground">
                    <div>/{m.yearly()}</div>
                  </div>
                </div>
              </div>
              <div className="text-left text-xs text-muted-foreground">
                {m.charged_x_per_year({
                  offer: offer.prices.yearly,
                })}
              </div>
            </div>
            {
              // loadingSub ? (
              //   <Skeleton className="w-40 h-[40px] bg-muted-foreground opacity-20" />
              // ) :
              user?.email && subscriptionPlan ? (
                offer.title === "Starter" ? (
                  <Link
                    href="/dashboard"
                    className={cn(
                      buttonVariants({
                        variant: "secondary",
                        size: "sm",
                      }),
                      "",
                    )}
                  >
                    {m.my_portfolio()}
                  </Link>
                ) : (
                  <BillingFormButton
                    offer={offer}
                    year={true}
                    subscriptionPlan={subscriptionPlan}
                  />
                )
              ) : (
                <Link
                  href="/signup?redirect=/checkout?yearly=true"
                  className="block"
                >
                  <Button size="sm" variant="secondary">
                    {m.pay_now()}
                  </Button>
                </Link>
              )
            }
          </div>

          <div className="md:w-1/2 min-h-[150px] items-start space-y-4  p-6">
            <h4 className="text-left text-muted font-bold">
              {m.feat_head_1()}
            </h4>
            <ul className="space-y-2 text-left text-sm font-medium leading-normal">
              <li className="flex items-start gap-x-3">
                <Icons.check className="size-5 shrink-0 text-purple-500" />
                <p className="text-muted ">{m.bland_vexed_llama_gaze()}</p>
              </li>
              <li className="flex items-start gap-x-3">
                <Icons.check className="size-5 shrink-0 text-purple-500" />
                <p className="text-muted ">{m.tasty_best_buzzard_revive()}</p>
              </li>
              <li className="flex items-start gap-x-3">
                <Icons.check className="size-5 shrink-0 text-purple-500" />
                <p className="text-muted ">{m.lime_real_dragonfly_amaze()}</p>
              </li>
            </ul>
          </div>

          <div className="md:w-1/2 min-h-[150px] items-start space-y-4  p-6">
            <h4 className="text-left text-muted font-bold">
              {m.feat_head_2()}
            </h4>
            <ul className="space-y-2 text-left text-sm font-medium leading-normal">
              <li className="flex items-start gap-x-3">
                <Icons.check className="size-5 shrink-0 text-purple-500" />
                <p className="text-muted ">{m.silly_last_kitten_tap()}</p>
              </li>
              <li className="flex items-start gap-x-3">
                <Icons.check className="size-5 shrink-0 text-purple-500" />
                <p className="text-muted ">{m.spicy_cool_frog_hope()}</p>
              </li>
              <li className="flex items-start gap-x-3">
                <Icons.check className="size-5 shrink-0 text-purple-500" />
                <p className="text-muted ">{m.cute_next_bat_play()}</p>
              </li>
              <li className="flex items-start gap-x-3">
                <Icons.check className="size-5 shrink-0 text-purple-500" />
                <p className="text-muted ">{m.orange_shy_gorilla_trim()}</p>
              </li>
            </ul>
          </div>

          <div className="md:w-1/2 min-h-[150px] items-start space-y-4  p-6">
            <h4 className="text-left text-muted font-bold">
              {m.feat_head_3()}
            </h4>
            <ul className="space-y-2 text-left text-sm font-medium leading-normal">
              <li className="flex items-start gap-x-3">
                <Icons.check className="size-5 shrink-0 text-purple-500" />
                <p className="text-muted ">{m.game_muddy_cheetah_amaze()}</p>
              </li>
              <li className="flex items-start gap-x-3">
                <Icons.check className="size-5 shrink-0 text-purple-500" />
                <p className="text-muted ">{m.low_soft_mongoose_gaze()}</p>
              </li>
              <li className="flex items-start gap-x-3">
                <Icons.check className="size-5 shrink-0 text-purple-500" />
                <p className="text-muted ">{m.new_fluffy_crow_seek()}</p>
              </li>
              <li className="flex items-start gap-x-3">
                <Icons.check className="size-5 shrink-0 text-purple-500" />
                <p className="text-muted ">{m.watery_bright_flamingo_sail()}</p>
              </li>
            </ul>
          </div>
        </div>
      );
    }
  };

  return (
    <section className="space-y-4">
      <div className="mt-10 grid md:grid-cols-3 gap-4">
        {pricingData(m).map((offer) => (
          <PricingCard offer={offer} key={offer.title} />
        ))}
      </div>
      {/*       <Link
        className="block"
        href={
          user?.email
            ? `/checkout?trial=true`
            : `/signup?redirect=/checkout?trial=true`
        }
      >
        <div className="bg-gradiant-dark relative overflow-hidden rounded-xl border shadow-sm w-full p-8">
          <FlickeringGrid
            className={cn(
              "[mask-image:radial-gradient(circle_at_left,white,transparent)] z-1 absolute inset-0 size-full",
            )}
            squareSize={2}
            gridGap={2}
            color="#fff"
            maxOpacity={0.6}
            flickerChance={0.4}
          />
          <div className="relative grid grid-cols-1 md:grid-cols-2 md:items-center ">
            <div className="items-center  py-4 ">
              <p className="text-center mix-blend-overlay  text-white text-5xl font-extrabold uppercase tracking-wider">
                {m.sad_basic_wallaby_shine()}
              </p>
            </div>

            <div className="justify-center text-white">
              <p className="font-bold ">{m.fit_patchy_ray_bless()}</p>
              <p className="text-sm">
                {m.patient_glad_impala_ripple()} <br />
                {m.ornate_many_anaconda_trust()}
              </p>
            </div>
          </div>
        </div>
      </Link> */}
    </section>
  );
}
